// frontend/admin-portal/src/components/treasury/ProfitBreakdown.jsx
import React from "react";
import { Panel, Spinner, Alert, formatMoney, Badge } from "@fiberhome/shared-ui";
import ProfitBreakdownTabs from "./ProfitBreakdownTabs";

function Row({ label, value, currency, tone = "default", bold = false, indent = 0 }) {
  const color =
    tone === "success" ? "#198754" :
    tone === "danger"  ? "#dc3545" :
    tone === "warning" ? "#f59e0b" :
    "var(--fh-text, #0f172a)";
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "0.4rem 0",
        paddingLeft: indent * 16,
        fontWeight: bold ? 700 : 400,
        fontSize: bold ? 14 : 13,
        color: bold ? color : "inherit",
      }}
    >
      <span style={{ color: bold ? color : "var(--fh-text-muted, #6b7280)" }}>
        {label}
      </span>
      <span style={{ fontWeight: bold ? 700 : 600, color, fontVariantNumeric: "tabular-nums" }}>
        {formatMoney(value, { symbol: currency })}
      </span>
    </div>
  );
}

export default function ProfitBreakdown({
  profit,
  loading,
  error,
  period = "this month",
}) {
  if (loading) {
    return (
      <Panel title="Profit" tone="success">
        <div style={{ textAlign: "center", padding: "1rem 0" }}>
          <Spinner size="sm" />
        </div>
      </Panel>
    );
  }

  if (error) {
    return (
      <Panel title="Profit" tone="success">
        <Alert variant="danger">{error}</Alert>
      </Panel>
    );
  }

  if (!profit) {
    return (
      <Panel title="Profit" tone="success">
        <div className="fh-dashboard-empty">No profit data available</div>
      </Panel>
    );
  }

  const cur = profit.currency;
  const isProfitable = profit.netProfit >= 0;
  const equityOk = Math.abs(profit.equityCheck.total - profit.netProfit) < 5;

  return (
    <Panel
      title="Profit"
      subtitle={`Period · ${period}`}
      tone="success"
      actions={
        <Badge variant={isProfitable ? "success" : "danger"}>
          {isProfitable ? "Profit" : "Loss"}
        </Badge>
      }
    >
      <div style={{ marginBottom: "0.5rem" }}>
        <div style={{ fontSize: 11.5, color: "var(--fh-text-muted, #6b7280)" }}>Net profit</div>
        <div
          style={{
            fontSize: 28,
            fontWeight: 700,
            color: isProfitable ? "#198754" : "#dc3545",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {formatMoney(profit.netProfit, { symbol: cur })}
        </div>
      </div>

      <div style={{ borderTop: "1px solid var(--fh-border, #eef0f3)", paddingTop: "0.5rem" }}>
        <Row label="Revenue" value={profit.revenue} currency={cur} tone="success" />
        <Row label="COGS" value={-profit.cogs} currency={cur} tone="danger" indent={1} />
        <Row label="Gross Profit" value={profit.grossProfit} currency={cur} bold />
        <Row label="Operating Expenses" value={-profit.operatingExpenses} currency={cur} tone="danger" indent={1} />
        <div style={{ borderTop: "1px solid var(--fh-border, #eef0f3)", marginTop: "0.35rem" }}>
          <Row
            label="Net Profit"
            value={profit.netProfit}
            currency={cur}
            bold
            tone={isProfitable ? "success" : "danger"}
          />
        </div>
      </div>

      {/* ── NEW: category / brand / item breakdown ──────── */}
      {profit.breakdown && (
        <div
          style={{
            marginTop: "0.75rem",
            paddingTop: "0.75rem",
            borderTop: "1px solid var(--fh-border, #eef0f3)",
          }}
        >
          <div
            style={{
              fontSize: 11.5,
              fontWeight: 600,
              color: "var(--fh-text-muted, #6b7280)",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              marginBottom: "0.35rem",
            }}
          >
            Breakdown
          </div>
          <ProfitBreakdownTabs
            breakdown={profit.breakdown}
            currency={cur}
            emptyMessage="No sales in this period"
          />
        </div>
      )}

      <details style={{ marginTop: "0.75rem", fontSize: 12 }}>
        <summary
          style={{ cursor: "pointer", color: "var(--fh-text-muted, #6b7280)", fontWeight: 600 }}
        >
          Balance sheet check {equityOk ? "✓" : "⚠"}
        </summary>
        <div style={{ paddingTop: "0.35rem", paddingLeft: 8 }}>
          <Row label="Δ Cash" value={profit.equityCheck.cashDelta} currency={cur} />
          <Row label="Δ Inventory" value={profit.equityCheck.inventoryDelta} currency={cur} />
          <Row label="Δ Receivables" value={profit.equityCheck.arDelta} currency={cur} />
          <Row label="Δ Payables" value={-profit.equityCheck.apDelta} currency={cur} />
          <Row
            label="Total"
            value={profit.equityCheck.total}
            currency={cur}
            bold
            tone={equityOk ? "success" : "warning"}
          />
        </div>
      </details>
    </Panel>
  );
}